// The member admin API's calls (A4; D23), for the Members tab: every answer read with the schema
// @grc/shared gives it, every change sent as JSON, `{}` when it says nothing more (D21).
import {
  MemberResponse,
  MembersResponse,
  OkResponse,
  OneTimePasswordResponse,
  type AdminMember,
  type CreateMemberRequest,
  type EditMemberRequest,
  type EmptyRequest,
} from '@grc/shared';
import { deleteJson, getJson, patchJson, postJson, readAs } from '../../api';

const MEMBERS = '/api/admin/members';

/** The body of disable, enable, the reset and remove: JSON all the same (D21). */
const NOTHING: EmptyRequest = {};

/** A member's route, and the action after it. */
function memberPath(id: string, action = ''): string {
  return `${MEMBERS}/${encodeURIComponent(id)}${action}`;
}

/** Every member, as an admin sees them (the API lists them by username). */
export async function listMembers(): Promise<AdminMember[]> {
  return (await readAs(MembersResponse, getJson(MEMBERS))).members;
}

/** A new member, and the one-time password the server made for them, given this once. */
export function createMember(body: CreateMemberRequest): Promise<OneTimePasswordResponse> {
  return readAs(OneTimePasswordResponse, postJson(MEMBERS, body));
}

/** Changes what `body` names (the display name, the username, the role); the member as now. */
export async function editMember(id: string, body: EditMemberRequest): Promise<AdminMember> {
  return (await readAs(MemberResponse, patchJson(memberPath(id), body))).member;
}

/** Disables the account, ending the member's sessions at once. */
export async function disableMember(id: string): Promise<AdminMember> {
  return (await readAs(MemberResponse, postJson(memberPath(id, '/disable'), NOTHING))).member;
}

export async function enableMember(id: string): Promise<AdminMember> {
  return (await readAs(MemberResponse, postJson(memberPath(id, '/enable'), NOTHING))).member;
}

/** A new one-time password, given this once; the member's sessions end. */
export function resetPassword(id: string): Promise<OneTimePasswordResponse> {
  return readAs(OneTimePasswordResponse, postJson(memberPath(id, '/password'), NOTHING));
}

/** Removes the member and everything of theirs. */
export async function removeMember(id: string): Promise<void> {
  await readAs(OkResponse, deleteJson(memberPath(id), NOTHING));
}
